import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  CheckCircle, 
  Circle, 
  Clock, 
  User, 
  AlertTriangle,
  Calendar,
  FileText,
  BarChart3,
  Target,
  TrendingUp,
  AlertCircle,
  Menu,
  X
} from 'lucide-react';
import { Project, Task } from '../types/project';
import BackgroundScene from './BackgroundScene';
import { projectService, taskService } from '../services/projectService';

const ProjectManagement: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Form states
  const [newProject, setNewProject] = useState({ name: '', description: '' });
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    deadline: '',
    assignee: '',
    priority: 'medium' as Task['priority']
  });
  const [editProject, setEditProject] = useState({ name: '', description: '' });

  // Load projects from database on component mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const projectsData = await projectService.getProjects();
        // Load tasks for each project
        const projectsWithTasks = await Promise.all(
          projectsData.map(async (project) => {
            const tasks = await taskService.getTasks(project.id);
            return {
              ...project,
              tasks: tasks.map((task: any) => ({
                ...task,
                createdAt: new Date(task.created_at),
                updatedAt: new Date(task.updated_at),
                deadline: task.deadline ? new Date(task.deadline) : undefined
              }))
            };
          })
        );
        setProjects(projectsWithTasks);
      } catch (error) {
        console.error('Error loading projects:', error);
      }
    };

    loadProjects();
  }, []);

  const createProject = async () => {
    if (!newProject.name.trim()) return;
    
    try {
      const projectData = await projectService.createProject({
        name: newProject.name.trim(),
        description: newProject.description.trim()
      } as any);

      const project: Project = {
        ...projectData,
        createdAt: new Date((projectData as any).created_at),
        updatedAt: new Date((projectData as any).updated_at),
        tasks: []
      };

      setProjects([project, ...projects]);
      setNewProject({ name: '', description: '' });
      setShowNewProjectModal(false);
    } catch (error) {
      console.error('Error creating project:', error);
    }
  };

  const createTask = async () => {
    if (!selectedProject || !newTask.title.trim()) return;

    try {
      const taskData = await taskService.createTask({
        project_id: selectedProject.id,
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        deadline: newTask.deadline ? new Date(newTask.deadline) : undefined,
        assignee: newTask.assignee.trim(),
        status: 'pending',
        priority: newTask.priority
      } as any);

      const task: Task = {
        ...taskData,
        createdAt: new Date((taskData as any).created_at),
        updatedAt: new Date((taskData as any).updated_at),
        deadline: taskData.deadline ? new Date(taskData.deadline) : undefined
      };

      const updatedProject = {
        ...selectedProject,
        tasks: [task, ...selectedProject.tasks],
        updatedAt: new Date()
      };

      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
      setSelectedProject(updatedProject);
      setNewTask({ title: '', description: '', deadline: '', assignee: '', priority: 'medium' });
      setShowNewTaskModal(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const updateTaskStatus = async (taskId: string, status: Task['status']) => {
    if (!selectedProject) return;

    try {
      await taskService.updateTask(taskId, { status });
      const updatedTasks = selectedProject.tasks.map(task =>
        task.id === taskId ? { ...task, status, updatedAt: new Date() } : task
      );

      const updatedProject = {
        ...selectedProject,
        tasks: updatedTasks,
        updatedAt: new Date()
      };

      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
      setSelectedProject(updatedProject);
    } catch (error) {
      console.error('Error updating task status:', error);
    }
  };

  const deleteProject = async (projectId: string) => {
    try {
      await projectService.deleteProject(projectId);
      setProjects(projects.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) {
        setSelectedProject(null);
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const deleteTask = async (taskId: string) => {
    if (!selectedProject) return;

    try {
      await taskService.deleteTask(taskId);
      const updatedTasks = selectedProject.tasks.filter(task => task.id !== taskId);
      const updatedProject = {
        ...selectedProject,
        tasks: updatedTasks,
        updatedAt: new Date()
      };

      setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
      setSelectedProject(updatedProject);
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const updateProject = () => {
    if (!selectedProject || !editProject.name.trim()) return;

    const updatedProject = {
      ...selectedProject,
      name: editProject.name.trim(),
      description: editProject.description.trim(),
      updatedAt: new Date()
    };

    setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
    setSelectedProject(updatedProject);
    setEditProject({ name: '', description: '' });
    setShowEditProjectModal(false);
  };

  const updateTask = () => {
    if (!selectedProject || !editingTask || !newTask.title.trim()) return;

    const updatedTasks = selectedProject.tasks.map(task =>
      task.id === editingTask.id ? {
        ...task,
        title: newTask.title.trim(),
        description: newTask.description.trim(),
        deadline: newTask.deadline ? new Date(newTask.deadline) : undefined,
        assignee: newTask.assignee.trim(),
        priority: newTask.priority,
        updatedAt: new Date()
      } : task
    );

    const updatedProject = {
      ...selectedProject,
      tasks: updatedTasks,
      updatedAt: new Date()
    };

    setProjects(projects.map(p => p.id === selectedProject.id ? updatedProject : p));
    setSelectedProject(updatedProject);
    setEditingTask(null);
    setNewTask({ title: '', description: '', deadline: '', assignee: '', priority: 'medium' });
    setShowEditTaskModal(false);
  };

  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'high': return 'text-red-500 bg-red-100/20';
      case 'medium': return 'text-yellow-500 bg-yellow-100/20';
      case 'low': return 'text-green-500 bg-green-100/20';
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-100/20';
      case 'in-progress': return 'text-blue-500 bg-blue-100/20';
      case 'pending': return 'text-gray-500 bg-gray-100/20';
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const isOverdue = (deadline: Date) => {
    return new Date() > deadline;
  };

  // Dashboard metrics calculation
  const getDashboardMetrics = () => {
    if (!selectedProject) return null;

    const totalTasks = selectedProject.tasks.length;
    const completedTasks = selectedProject.tasks.filter(t => t.status === 'completed').length;
    const pendingTasks = selectedProject.tasks.filter(t => t.status === 'pending').length;
    const inProgressTasks = selectedProject.tasks.filter(t => t.status === 'in-progress').length;
    const overdueTasks = selectedProject.tasks.filter(t => 
      t.deadline && isOverdue(t.deadline) && t.status !== 'completed'
    ).length;
    const highPriorityTasks = selectedProject.tasks.filter(t => t.priority === 'high').length;
    const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

    return {
      totalTasks,
      completedTasks,
      pendingTasks,
      inProgressTasks,
      overdueTasks,
      highPriorityTasks,
      completionRate
    };
  };

  const metrics = getDashboardMetrics();

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Three.js Background */}
      <BackgroundScene />
      
      {/* Mobile Header */}
      <motion.div 
        initial={{ y: -50 }}
        animate={{ y: 0 }}
        className="lg:hidden relative z-20 glass-effect p-4 border-b border-modern-700/50"
      >
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Project Management</h1>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 bg-modern-700/50 hover:bg-modern-700/70 rounded-lg text-white transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </motion.button>
        </div>
      </motion.div>

      {/* Main Interface */}
      <div className="relative z-10 flex h-[calc(100vh-4rem)] lg:h-screen overflow-hidden">
        {/* Sidebar Overlay for Mobile */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-30"
              onClick={() => setIsMobileMenuOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Sidebar */}
        <AnimatePresence>
          <motion.div 
            initial={{ x: -300 }}
            animate={{ 
              x: 0
            }}
            exit={{ x: -300 }}
            className="hidden lg:flex lg:w-80 glass-effect border-r border-modern-700/50 p-4 flex-col overflow-hidden"
          >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Projects</h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowNewProjectModal(true)}
              className="p-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-white transition-colors"
            >
              <Plus className="w-5 h-5" />
            </motion.button>
          </div>

          <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
            <AnimatePresence>
              {projects.map((project) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                    selectedProject?.id === project.id
                      ? 'bg-primary-500/20 border border-primary-400/50'
                      : 'bg-modern-700/30 hover:bg-modern-700/50 border border-transparent'
                  }`}
                  onClick={() => setSelectedProject(project)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-semibold text-white truncate">{project.name}</h3>
                      {project.description && (
                        <p className="text-sm text-white/70 truncate mt-1">{project.description}</p>
                      )}
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="text-xs text-white/60">
                          {project.tasks.length} tasks
                        </span>
                        <span className="text-xs text-white/60">
                          {project.tasks.filter(t => t.status === 'completed').length} completed
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditProject({ name: project.name, description: project.description || '' });
                        setSelectedProject(project);
                        setShowEditProjectModal(true);
                      }}
                      className="p-1 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          </motion.div>
        </AnimatePresence>

        {/* Mobile Sidebar */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              className="lg:hidden fixed inset-y-0 left-0 z-40 w-80 glass-effect border-r border-modern-700/50 p-4 flex flex-col"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Projects</h2>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNewProjectModal(true)}
                  className="p-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-white transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </motion.button>
              </div>

              <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
                <AnimatePresence>
                  {projects.map((project) => (
                    <motion.div
                      key={project.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                        selectedProject?.id === project.id
                          ? 'bg-primary-500/20 border border-primary-400/50'
                          : 'bg-modern-700/30 hover:bg-modern-700/50 border border-transparent'
                      }`}
                      onClick={() => {
                        setSelectedProject(project);
                        setIsMobileMenuOpen(false); // Close mobile menu when project is selected
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-white truncate">{project.name}</h3>
                          {project.description && (
                            <p className="text-sm text-white/70 truncate mt-1">{project.description}</p>
                          )}
                          <div className="flex items-center space-x-2 mt-2">
                            <span className="text-xs text-white/60">
                              {project.tasks.length} tasks
                            </span>
                            <span className="text-xs text-white/60">
                              {project.tasks.filter(t => t.status === 'completed').length} completed
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedProject(project);
                            setShowEditProjectModal(true);
                          }}
                          className="p-1 text-white/60 hover:text-white hover:bg-white/10 rounded transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Content */}
        <div className="flex-1 flex flex-col lg:mt-0 mt-0 overflow-hidden">
          {selectedProject ? (
            <>
              {/* Project Header - Fixed on mobile, scrollable on desktop */}
              <motion.div 
                initial={{ y: -50 }}
                animate={{ y: 0 }}
                className="glass-effect p-4 border-b border-modern-700/50 lg:flex-shrink-0"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 lg:hidden mb-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setIsMobileMenuOpen(true)}
                        className="p-2 bg-modern-700/50 hover:bg-modern-700/70 rounded-lg text-white transition-colors"
                      >
                        <Menu className="w-5 h-5" />
                      </motion.button>
                      <h1 className="text-xl lg:text-2xl font-bold text-white">{selectedProject.name}</h1>
                    </div>
                    <h1 className="hidden lg:block text-2xl font-bold text-white">{selectedProject.name}</h1>
                    {selectedProject.description && (
                      <p className="text-white/70 mt-1">{selectedProject.description}</p>
                    )}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-2 space-y-1 sm:space-y-0">
                      <span className="text-sm text-white/60">
                        Created {formatDate(selectedProject.createdAt)}
                      </span>
                      <span className="text-sm text-white/60">
                        Last updated {formatDate(selectedProject.updatedAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0">
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowNewTaskModal(true)}
                      className="px-4 py-2 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-medium transition-colors"
                    >
                      <Plus className="w-4 h-4 mr-2 inline" />
                      Add Task
                    </motion.button>
                    <button
                      onClick={() => deleteProject(selectedProject.id)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {/* Dashboard - Part of scrollable content on mobile */}
                {metrics && (
                  <motion.div 
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    className="glass-effect p-3 lg:p-4 border-b border-modern-700/50"
                  >
                    <div className="flex items-center space-x-2 mb-3 lg:mb-4">
                      <BarChart3 className="w-5 h-5 text-primary-400" />
                      <h2 className="text-lg font-semibold text-white">Project Dashboard</h2>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 lg:gap-4">
                      {/* Total Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-blue-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">Total Tasks</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.totalTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Completed Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 text-green-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">Completed</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.completedTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Pending Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <Circle className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">Pending</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.pendingTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* In Progress Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 lg:w-5 lg:h-5 text-primary-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">In Progress</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.inProgressTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Overdue Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="w-4 h-4 lg:w-5 lg:h-5 text-red-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">Overdue</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.overdueTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* High Priority Tasks */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <Target className="w-4 h-4 lg:w-5 lg:h-5 text-orange-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">High Priority</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.highPriorityTasks}</p>
                          </div>
                        </div>
                      </motion.div>

                      {/* Completion Rate */}
                      <motion.div 
                        whileHover={{ scale: 1.02 }}
                        className="bg-modern-700/30 p-2 lg:p-4 rounded-xl border border-modern-600/50"
                      >
                        <div className="flex items-center space-x-2">
                          <TrendingUp className="w-4 h-4 lg:w-5 lg:h-5 text-green-400" />
                          <div>
                            <p className="text-xs lg:text-sm text-white/60">Completion</p>
                            <p className="text-lg lg:text-2xl font-bold text-white">{metrics.completionRate}%</p>
                          </div>
                        </div>
                      </motion.div>
                    </div>

                    {/* Progress Bar */}
                    {metrics.totalTasks > 0 && (
                      <div className="mt-3 lg:mt-4">
                        <div className="flex items-center justify-between text-sm text-white/60 mb-2">
                          <span>Progress</span>
                          <span>{metrics.completedTasks} of {metrics.totalTasks} tasks completed</span>
                        </div>
                        <div className="w-full bg-modern-700/50 rounded-full h-2">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${metrics.completionRate}%` }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                            className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full"
                          />
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Tasks List */}
                <div className="p-4">
                <div className="max-w-4xl mx-auto space-y-4">
                  <AnimatePresence>
                    {selectedProject.tasks.map((task) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="glass-effect p-4 rounded-xl border border-modern-700/50"
                      >
                        <div className="flex items-start space-x-4">
                          <button
                            onClick={() => updateTaskStatus(task.id, task.status === 'completed' ? 'pending' : 'completed')}
                            className="mt-1 p-1 text-primary-500 hover:text-primary-400 transition-colors"
                          >
                            {task.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5" />
                            ) : (
                              <Circle className="w-5 h-5" />
                            )}
                          </button>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h3 className={`font-semibold text-lg ${
                                  task.status === 'completed' ? 'line-through text-white/60' : 'text-white'
                                }`}>
                                  {task.title}
                                </h3>
                                {task.description && (
                                  <p className={`mt-2 ${
                                    task.status === 'completed' ? 'text-white/50' : 'text-white/70'
                                  }`}>
                                    {task.description}
                                  </p>
                                )}
                                
                                <div className="flex items-center space-x-4 mt-3">
                                  {task.deadline && (
                                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${
                                      isOverdue(task.deadline) && task.status !== 'completed'
                                        ? 'text-red-400 bg-red-100/20'
                                        : 'text-white/60 bg-white/10'
                                    }`}>
                                      <Calendar className="w-3 h-3" />
                                      <span>{formatDate(task.deadline)}</span>
                                      {isOverdue(task.deadline) && task.status !== 'completed' && (
                                        <AlertTriangle className="w-3 h-3 ml-1" />
                                      )}
                                    </div>
                                  )}
                                  
                                  {task.assignee && (
                                    <div className="flex items-center space-x-1 px-2 py-1 rounded-full text-xs text-white/60 bg-white/10">
                                      <User className="w-3 h-3" />
                                      <span>{task.assignee}</span>
                                    </div>
                                  )}
                                  
                                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getPriorityColor(task.priority)}`}>
                                    <span className="capitalize">{task.priority}</span>
                                  </div>
                                  
                                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getStatusColor(task.status)}`}>
                                    <span className="capitalize">{task.status.replace('-', ' ')}</span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => {
                                    setEditingTask(task);
                                    setNewTask({
                                      title: task.title,
                                      description: task.description || '',
                                      deadline: task.deadline ? task.deadline.toISOString().split('T')[0] : '',
                                      assignee: task.assignee || '',
                                      priority: task.priority
                                    });
                                    setShowEditTaskModal(true);
                                  }}
                                  className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => deleteTask(task.id)}
                                  className="p-2 text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {selectedProject.tasks.length === 0 && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="text-center py-12"
                    >
                      <FileText className="w-16 h-16 text-white/30 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-white/60 mb-2">No tasks yet</h3>
                      <p className="text-white/40 mb-4">Get started by adding your first task</p>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setShowNewTaskModal(true)}
                        className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-medium transition-colors"
                      >
                        <Plus className="w-4 h-4 mr-2 inline" />
                        Add First Task
                      </motion.button>
                    </motion.div>
                  )}
                </div>
              </div>
            </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center overflow-y-auto">
              <div className="text-center">
                <FileText className="w-24 h-24 text-white/30 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-white/60 mb-2">No Project Selected</h2>
                <p className="text-white/40 mb-6">Select a project from the sidebar or create a new one</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowNewProjectModal(true)}
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 rounded-lg text-white font-medium transition-colors"
                >
                  <Plus className="w-5 h-5 mr-2 inline" />
                  Create New Project
                </motion.button>
                

              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      <AnimatePresence>
        {showNewProjectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewProjectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-effect p-6 rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Create New Project</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter project name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Description (Optional)</label>
                  <textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter project description"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowNewProjectModal(false)}
                    className="flex-1 px-4 py-2 bg-modern-700/50 hover:bg-modern-700/70 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createProject}
                    className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Create Project
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Task Modal */}
      <AnimatePresence>
        {showNewTaskModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowNewTaskModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-effect p-6 rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Add New Task</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Task Title</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter task title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Description (Optional)</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter task description"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Deadline (Optional)</label>
                    <input
                      type="date"
                      value={newTask.deadline}
                      onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                      className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Assignee (Optional)</label>
                    <input
                      type="text"
                      value={newTask.assignee}
                      onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                      className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      placeholder="Enter assignee name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as Task['priority'] })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowNewTaskModal(false)}
                    className="flex-1 px-4 py-2 bg-modern-700/50 hover:bg-modern-700/70 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={createTask}
                    className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Add Task
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Project Modal */}
      <AnimatePresence>
        {showEditProjectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditProjectModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-effect p-6 rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Edit Project</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Project Name</label>
                  <input
                    type="text"
                    value={editProject.name}
                    onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter project name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Description (Optional)</label>
                  <textarea
                    value={editProject.description}
                    onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter project description"
                    rows={3}
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowEditProjectModal(false)}
                    className="flex-1 px-4 py-2 bg-modern-700/50 hover:bg-modern-700/70 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateProject}
                    className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Update Project
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Task Modal */}
      <AnimatePresence>
        {showEditTaskModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowEditTaskModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-effect p-6 rounded-xl w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-white mb-4">Edit Task</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Task Title</label>
                  <input
                    type="text"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter task title"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Description (Optional)</label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    placeholder="Enter task description"
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Deadline (Optional)</label>
                    <input
                      type="date"
                      value={newTask.deadline}
                      onChange={(e) => setNewTask({ ...newTask, deadline: e.target.value })}
                      className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-2">Assignee (Optional)</label>
                    <input
                      type="text"
                      value={newTask.assignee}
                      onChange={(e) => setNewTask({ ...newTask, assignee: e.target.value })}
                      className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-primary-400"
                      placeholder="Enter assignee name"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-white/80 mb-2">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as Task['priority'] })}
                    className="w-full p-3 bg-modern-700/50 border border-modern-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-400"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowEditTaskModal(false)}
                    className="flex-1 px-4 py-2 bg-modern-700/50 hover:bg-modern-700/70 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={updateTask}
                    className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
                  >
                    Update Task
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectManagement; 